/*
 * Confidentialite — GSF Arbitral AI Vitrine
 * Design: Marine #0D1B2A, Or métallique #C9A84C, Cormorant Garamond + Inter
 * Contenu adapté de la politique de confidentialité de eji2.org
 * Adaptations : nom "GSF Arbitral AI" et hébergeur "Vercel, Inc." (remplace Netlify)
 */
import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const MARINE = "oklch(0.175 0.038 243)";
const GOLD = "oklch(0.695 0.105 68)";

export default function Confidentialite() {
  return (
    <div className="min-h-screen" style={{ background: "oklch(1 0 0)" }}>
      <Navbar />

      {/* Hero de page */}
      <section
        className="pt-28 pb-16"
        style={{ background: MARINE }}
      >
        <div className="container">
          <div className="gsf-badge mb-4">Protection des Données</div>
          <h1
            className="font-bold leading-tight"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(2rem, 5vw, 3.5rem)",
              color: "oklch(1 0 0)",
              lineHeight: 1.1,
            }}
          >
            Politique de Confidentialité
          </h1>
          <p className="mt-3 text-sm" style={{ color: "oklch(0.70 0.01 243)" }}>
            Dernière mise à jour : 10 mars 2026
          </p>
        </div>
      </section>

      <div className="gsf-gold-line" />

      {/* Contenu */}
      <section className="py-16">
        <div className="container max-w-3xl mx-auto">

          <Link
            href="/"
            className="inline-flex items-center gap-2 text-sm mb-10 no-underline transition-colors"
            style={{ color: GOLD }}
          >
            <ArrowLeft size={14} />
            Retour à l'accueil
          </Link>

          <p className="text-base leading-relaxed mb-10" style={{ color: "oklch(0.45 0.01 243)" }}>
            La présente politique de confidentialité décrit les modalités selon lesquelles GSF Arbitral AI collecte, traite, conserve et protège les données à caractère personnel des utilisateurs de la plateforme, en conformité avec la réglementation applicable en matière de protection des données.
          </p>

          {[
            {
              num: "1",
              title: "Responsable du traitement",
              content: (
                <>
                  <p className="text-sm leading-relaxed mb-2" style={{ color: "oklch(0.45 0.01 243)" }}>
                    L'entité responsable du traitement des données est :
                  </p>
                  <div
                    className="p-5 rounded-sm text-sm leading-relaxed"
                    style={{ background: "oklch(0.97 0.005 243)", border: "1px solid oklch(0.90 0.005 243)" }}
                  >
                    <p className="font-semibold mb-1" style={{ color: MARINE }}>ÉTHIQUE JURIDIQUE 2.0. INTERNATIONALE (EJI 2.0) W941020107</p>
                    <p style={{ color: "oklch(0.45 0.01 243)" }}>Siège social : 11 quai Anatole France, 75007 Paris. Tel. + 33 1 48 98 02 07</p>
                    <p style={{ color: "oklch(0.45 0.01 243)" }}>
                      E-mail. :{" "}
                      <a href="mailto:contact@eji2.org" style={{ color: GOLD }}>
                        contact@eji2.org
                      </a>
                    </p>
                  </div>
                </>
              ),
            },
            {
              num: "2",
              title: "Données susceptibles d'être collectées",
              content: (
                <>
                  <p className="text-sm leading-relaxed mb-3" style={{ color: "oklch(0.45 0.01 243)" }}>
                    Dans le cadre de l'accès et de l'utilisation de la plateforme GSF Arbitral AI, les données suivantes peuvent être recueillies :
                  </p>
                  <ul className="text-sm leading-relaxed space-y-2 pl-5" style={{ color: "oklch(0.45 0.01 243)", listStyleType: "disc" }}>
                    <li>Coordonnées personnelles : nom, prénom, adresse email, numéro de téléphone ;</li>
                    <li>Informations professionnelles : cabinet, société ou institution de rattachement, qualité (avocat, arbitre, partie) ;</li>
                    <li>Contenu des messages ou documents transmis via le site ou les formulaires de contact ;</li>
                    <li>Données de navigation technique, adresse IP et journaux de connexion ;</li>
                    <li>Statistiques d'audience anonymisées, lorsque la mesure est activée.</li>
                  </ul>
                </>
              ),
            },
            {
              num: "3",
              title: "Finalités du traitement",
              content: (
                <p className="text-sm leading-relaxed" style={{ color: "oklch(0.45 0.01 243)" }}>
                  Les informations collectées servent à répondre aux demandes de contact, à traiter les demandes de devis ou d'accès à la plateforme d'arbitrage, à assurer le suivi professionnel des dossiers, à améliorer la qualité du service et à respecter les obligations légales et réglementaires applicables, notamment dans le cadre de l'Acte Uniforme OHADA relatif au droit de l'arbitrage.
                </p>
              ),
            },
            {
              num: "4",
              title: "Base légale des traitements",
              content: (
                <p className="text-sm leading-relaxed" style={{ color: "oklch(0.45 0.01 243)" }}>
                  Les opérations de traitement reposent, selon le cas, sur le consentement explicite de l'utilisateur, sur l'exécution de mesures précontractuelles demandées par l'utilisateur, sur l'intérêt légitime de GSF Arbitral AI à assurer la gestion et la promotion de ses services, ou sur le respect des obligations légales et réglementaires en vigueur.
                </p>
              ),
            },
            {
              num: "5",
              title: "Durée de conservation",
              content: (
                <p className="text-sm leading-relaxed" style={{ color: "oklch(0.45 0.01 243)" }}>
                  Les données sont conservées pendant une durée proportionnée à la finalité du traitement. Les demandes de contact et de devis sont conservées pour une durée maximale de 12 mois. Les journaux techniques et logs de connexion sont conservés pour une durée maximale de 12 mois. Les données relatives aux dossiers d'arbitrage sont conservées conformément aux obligations légales et conventionnelles applicables.
                </p>
              ),
            },
            {
              num: "6",
              title: "Destinataires des données",
              content: (
                <p className="text-sm leading-relaxed" style={{ color: "oklch(0.45 0.01 243)" }}>
                  Les données collectées restent internes à GSF Arbitral AI et à ÉTHIQUE JURIDIQUE 2.0. INTERNATIONALE (EJI 2.0) W941020107, et ne sont transmises qu'aux prestataires techniques strictement nécessaires à la fourniture du service (hébergement, sécurité, messagerie transactionnelle). Aucune cession commerciale à des tiers n'est effectuée.
                </p>
              ),
            },
            {
              num: "7",
              title: "Hébergement et services techniques",
              content: (
                <>
                  <p className="text-sm leading-relaxed mb-3" style={{ color: "oklch(0.45 0.01 243)" }}>
                    L'hébergement du site est assuré par :
                  </p>
                  <div
                    className="p-5 rounded-sm text-sm leading-relaxed"
                    style={{ background: "oklch(0.97 0.005 243)", border: "1px solid oklch(0.90 0.005 243)" }}
                  >
                    <p className="font-semibold mb-1" style={{ color: MARINE }}>Vercel, Inc.</p>
                    <p style={{ color: "oklch(0.45 0.01 243)" }}>340 Pine Street, Suite 701, San Francisco, CA 94104, États-Unis</p>
                    <p style={{ color: "oklch(0.45 0.01 243)" }}>
                      Site web :{" "}
                      <a href="https://www.vercel.com" target="_blank" rel="noopener noreferrer" style={{ color: GOLD }}>
                        www.vercel.com
                      </a>
                    </p>
                  </div>
                  <p className="text-sm leading-relaxed mt-3" style={{ color: "oklch(0.45 0.01 243)" }}>
                    D'autres services techniques complémentaires (authentification, base de données, mesure d'audience) peuvent soutenir la gestion éditoriale et fonctionnelle de la plateforme, dans le respect des présentes dispositions.
                  </p>
                </>
              ),
            },
            {
              num: "8",
              title: "Cookies et traceurs",
              content: (
                <p className="text-sm leading-relaxed" style={{ color: "oklch(0.45 0.01 243)" }}>
                  La plateforme emploie uniquement des cookies strictement nécessaires à son bon fonctionnement technique, à la sécurité de la navigation et, le cas échéant, à la mesure d'audience anonymisée. Aucun traceur publicitaire n'est déposé sans consentement explicite préalable de l'utilisateur.
                </p>
              ),
            },
            {
              num: "9",
              title: "Droits des utilisateurs",
              content: (
                <>
                  <p className="text-sm leading-relaxed mb-3" style={{ color: "oklch(0.45 0.01 243)" }}>
                    Conformément à la réglementation applicable, chaque utilisateur dispose des droits suivants sur ses données personnelles :
                  </p>
                  <ul className="text-sm leading-relaxed space-y-2 pl-5 mb-3" style={{ color: "oklch(0.45 0.01 243)", listStyleType: "disc" }}>
                    <li>Droit d'accès, de rectification et d'effacement ;</li>
                    <li>Droit de limitation et d'opposition au traitement ;</li>
                    <li>Droit à la portabilité des données ;</li>
                    <li>Droit de retirer son consentement à tout moment ;</li>
                    <li>Droit d'introduire une réclamation auprès de l'autorité de contrôle compétente.</li>
                  </ul>
                  <p className="text-sm leading-relaxed" style={{ color: "oklch(0.45 0.01 243)" }}>
                    L'exercice de ces droits s'effectue par courrier électronique à{" "}
                    <a href="mailto:contact@eji2.org" style={{ color: GOLD }}>
                      contact@eji2.org
                    </a>.
                  </p>
                </>
              ),
            },
            {
              num: "10",
              title: "Sécurité",
              content: (
                <p className="text-sm leading-relaxed" style={{ color: "oklch(0.45 0.01 243)" }}>
                  GSF Arbitral AI met en œuvre des mesures techniques et organisationnelles appropriées pour préserver la confidentialité, l'intégrité et la disponibilité des données, et les protéger contre tout accès non autorisé, perte, altération ou traitement non conforme. Ces mesures incluent notamment le chiffrement des échanges, le contrôle des accès et l'hébergement dans des infrastructures conformes aux standards de sécurité applicables.
                </p>
              ),
            },
            {
              num: "11",
              title: "Liens externes",
              content: (
                <p className="text-sm leading-relaxed" style={{ color: "oklch(0.45 0.01 243)" }}>
                  GSF Arbitral AI décline toute responsabilité concernant les politiques de confidentialité, le contenu et les pratiques des sites tiers accessibles via des liens externes présents sur la plateforme. Il appartient à l'utilisateur de consulter les politiques propres à ces sites.
                </p>
              ),
            },
            {
              num: "12",
              title: "Mise à jour de la politique",
              content: (
                <p className="text-sm leading-relaxed" style={{ color: "oklch(0.45 0.01 243)" }}>
                  La présente politique de confidentialité peut être modifiée à tout moment afin de tenir compte des évolutions légales, réglementaires, techniques ou organisationnelles.
                  <br /><br />
                  <strong style={{ color: MARINE }}>Dernière mise à jour :</strong> 10 mars 2026
                </p>
              ),
            },
          ].map((section) => (
            <div
              key={section.num}
              className="mb-10 pb-10"
              style={{ borderBottom: "1px solid oklch(0.92 0.004 243)" }}
            >
              <h2
                className="text-2xl font-bold mb-5"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: MARINE }}
              >
                {section.num}. {section.title}
              </h2>
              {section.content}
            </div>
          ))}

          <div className="mt-4 pt-6 flex flex-wrap gap-4 text-sm" style={{ borderTop: "1px solid oklch(0.92 0.004 243)" }}>
            <Link href="/" style={{ color: GOLD, textDecoration: "none" }}>
              ← Retour à l'accueil
            </Link>
            <Link href="/mentions-legales" style={{ color: GOLD, textDecoration: "none" }}>
              Mentions légales
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
